import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Customer, Product, Supplier, Staff, Project, Quotation, Invoice, FinanceRecord, ActivityLog, Warranty, Payment } from '../types';
import { sampleCustomers, sampleProducts, sampleSuppliers, sampleStaff, sampleProjects, sampleQuotations, sampleInvoices, sampleFinance, sampleActivityLogs, sampleWarranties } from '../data/seedData';
import { generateId } from '../utils/helpers';
import { useBusinessSettingsStore } from './businessSettingsStore';

interface AdminStore {
  // Auth
  isAuthenticated: boolean;
  currentUser: string;
  sessionExpiry: number | null;
  /** Validates credentials without changing auth state (used for step-1 of 2-step login). */
  verifyCredentials: (username: string, password: string) => boolean;
  /** Sets isAuthenticated=true. Call only after credentials AND PIN are verified. */
  login: (username: string, password: string) => boolean;
  logout: () => void;
  extendSession: () => void;

  // Customers
  customers: Customer[];
  addCustomer: (customer: Customer) => void;
  updateCustomer: (id: string, data: Partial<Customer>) => void;
  deleteCustomer: (id: string) => void;

  // Products
  products: Product[];
  addProduct: (product: Product) => void;
  updateProduct: (id: string, data: Partial<Product>) => void;
  deleteProduct: (id: string) => void;
  duplicateProduct: (id: string) => void;

  // Suppliers
  suppliers: Supplier[];
  addSupplier: (supplier: Supplier) => void;
  updateSupplier: (id: string, data: Partial<Supplier>) => void;
  deleteSupplier: (id: string) => void;

  // Staff
  staff: Staff[];
  addStaff: (member: Staff) => void;
  updateStaff: (id: string, data: Partial<Staff>) => void;
  deleteStaff: (id: string) => void;

  // Projects
  projects: Project[];
  addProject: (project: Project) => void;
  updateProject: (id: string, data: Partial<Project>) => void;
  deleteProject: (id: string) => void;

  // Quotations
  quotations: Quotation[];
  addQuotation: (quotation: Quotation) => void;
  updateQuotation: (id: string, data: Partial<Quotation>) => void;
  convertToInvoice: (quotationId: string) => string | null;

  // Invoices
  invoices: Invoice[];
  addInvoice: (invoice: Invoice) => void;
  updateInvoice: (id: string, data: Partial<Invoice>) => void;
  addPayment: (invoiceId: string, payment: Payment) => void;

  // Finance
  financeRecords: FinanceRecord[];
  addFinanceRecord: (record: FinanceRecord) => void;

  // Warranties
  warranties: Warranty[];
  addWarranty: (warranty: Warranty) => void;
  updateWarranty: (id: string, data: Partial<Warranty>) => void;

  // Activity Logs
  activityLogs: ActivityLog[];
  addActivityLog: (log: ActivityLog) => void;

  // Sidebar
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;

  // Theme
  theme: 'light' | 'dark';
  toggleTheme: () => void;

  // Seed reset
  resetToSeedData: () => void;
  // Business data reset
  resetBusinessData: () => void;
}

const defaultUser = {
  id: 'USR-001',
  name: 'Admin',
  username: 'admin',
  email: 'admin@k-tec.lk',
  role: 'admin' as const,
  isActive: true,
};

const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes

export const useAdminStore = create<AdminStore>()(
  persist(
    (set, get) => ({
      // Auth
      isAuthenticated: false,
      currentUser: 'Admin',
      sessionExpiry: null,
      verifyCredentials: (username: string, password: string) => {
        // Read password dynamically from businessSettingsStore
        const storedPassword = useBusinessSettingsStore.getState().adminPassword;
        return username === 'admin' && password === storedPassword;
      },
      login: (username: string, password: string) => {
        const storedPassword = useBusinessSettingsStore.getState().adminPassword;
        if (username === 'admin' && password === storedPassword) {
          const expiry = Date.now() + SESSION_TIMEOUT;
          set({ isAuthenticated: true, currentUser: defaultUser.name, sessionExpiry: expiry });
          get().addActivityLog({
            id: generateId(), timestamp: new Date().toISOString(), user: 'Admin',
            action: 'Login', details: 'Admin portal accessed', status: 'success',
          });
          return true;
        }
        return false;
      },
      logout: () => {
        set({ isAuthenticated: false, currentUser: '', sessionExpiry: null });
      },
      extendSession: () => {
        const expiry = Date.now() + SESSION_TIMEOUT;
        set({ sessionExpiry: expiry });
      },

      // Customers
      customers: sampleCustomers,
      addCustomer: (customer) => {
        set((s) => ({ customers: [customer, ...s.customers] }));
        get().addActivityLog({
          id: generateId(), timestamp: new Date().toISOString(), user: get().currentUser,
          action: 'Customer Created', details: `New customer: ${customer.name}`, status: 'success',
        });
      },
      updateCustomer: (id, data) => set((s) => ({
        customers: s.customers.map((c) => (c.id === id ? { ...c, ...data, updatedAt: new Date().toISOString() } : c)),
      })),
      deleteCustomer: (id) => {
        const customer = get().customers.find(c => c.id === id);
        set((s) => ({ customers: s.customers.filter((c) => c.id !== id) }));
        if (customer) {
          get().addActivityLog({
            id: generateId(), timestamp: new Date().toISOString(), user: get().currentUser,
            action: 'Customer Deleted', details: `Removed: ${customer.name}`, status: 'warning',
          });
        }
      },

      // Products
      products: sampleProducts,
      addProduct: (product) => {
        set((s) => ({ products: [product, ...s.products] }));
        get().addActivityLog({
          id: generateId(), timestamp: new Date().toISOString(), user: get().currentUser,
          action: 'Product Added', details: `${product.name} (SKU: ${product.sku})`, status: 'success',
        });
      },
      updateProduct: (id, data) => set((s) => ({
        products: s.products.map((p) => (p.id === id ? { ...p, ...data, updatedAt: new Date().toISOString() } : p)),
      })),
      deleteProduct: (id) => set((s) => ({ products: s.products.filter((p) => p.id !== id) })),
      duplicateProduct: (id) => {
        const product = get().products.find((p) => p.id === id);
        if (product) {
          const newProduct = { ...product, id: generateId(), name: `${product.name} (Copy)`, sku: `${product.sku}-COPY`, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
          set((s) => ({ products: [newProduct, ...s.products] }));
        }
      },

      // Suppliers
      suppliers: sampleSuppliers,
      addSupplier: (supplier) => {
        set((s) => ({ suppliers: [supplier, ...s.suppliers] }));
        get().addActivityLog({
          id: generateId(), timestamp: new Date().toISOString(), user: get().currentUser,
          action: 'Supplier Added', details: `${supplier.name} - ${supplier.company}`, status: 'success',
        });
      },
      updateSupplier: (id, data) => set((s) => ({
        suppliers: s.suppliers.map((sp) => (sp.id === id ? { ...sp, ...data } : sp)),
      })),
      deleteSupplier: (id) => {
        const supplier = get().suppliers.find(s => s.id === id);
        set((s) => ({ suppliers: s.suppliers.filter((sp) => sp.id !== id) }));
        if (supplier) {
          get().addActivityLog({
            id: generateId(), timestamp: new Date().toISOString(), user: get().currentUser,
            action: 'Supplier Deleted', details: `Removed: ${supplier.name}`, status: 'warning',
          });
        }
      },

      // Staff
      staff: sampleStaff,
      addStaff: (member) => {
        set((s) => ({ staff: [member, ...s.staff] }));
        get().addActivityLog({
          id: generateId(), timestamp: new Date().toISOString(), user: get().currentUser,
          action: 'Staff Added', details: `${member.name} - ${member.role}`, status: 'success',
        });
      },
      updateStaff: (id, data) => set((s) => ({
        staff: s.staff.map((st) => (st.id === id ? { ...st, ...data } : st)),
      })),
      deleteStaff: (id) => set((s) => ({ staff: s.staff.filter((st) => st.id !== id) })),

      // Projects
      projects: sampleProjects,
      addProject: (project) => {
        set((s) => ({ projects: [project, ...s.projects] }));
        get().addActivityLog({
          id: generateId(), timestamp: new Date().toISOString(), user: get().currentUser,
          action: 'Project Created', details: `${project.name}`, status: 'success',
        });
      },
      updateProject: (id, data) => set((s) => ({
        projects: s.projects.map((p) => (p.id === id ? { ...p, ...data } : p)),
      })),
      deleteProject: (id) => {
        const project = get().projects.find(p => p.id === id);
        set((s) => ({ projects: s.projects.filter((p) => p.id !== id) }));
        if (project) {
          get().addActivityLog({
            id: generateId(), timestamp: new Date().toISOString(), user: get().currentUser,
            action: 'Project Deleted', details: `Removed: ${project.name}`, status: 'warning',
          });
        }
      },

      // Quotations
      quotations: sampleQuotations,
      addQuotation: (quotation) => {
        set((s) => ({ quotations: [quotation, ...s.quotations] }));
        get().addActivityLog({
          id: generateId(), timestamp: new Date().toISOString(), user: get().currentUser,
          action: 'Quotation Created', details: `${quotation.number} for ${quotation.customerName}`, status: 'success',
        });
      },
      updateQuotation: (id, data) => set((s) => ({
        quotations: s.quotations.map((q) => (q.id === id ? { ...q, ...data, updatedAt: new Date().toISOString() } : q)),
      })),
      convertToInvoice: (quotationId) => {
        const quotation = get().quotations.find((q) => q.id === quotationId);
        if (!quotation || quotation.status !== 'approved') return null;

        const newInvoiceId = generateId();
        const newInvoice: Invoice = {
          id: newInvoiceId,
          number: `INV-${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(Math.floor(Math.random() * 9999) + 1).padStart(4, '0')}`,
          customerId: quotation.customerId,
          customerName: quotation.customerName,
          quotationId: quotation.id,
          items: quotation.items.map((qi) => ({ ...qi, id: `II-${generateId()}` })),
          subtotal: quotation.subtotal,
          discount: quotation.discount,
          discountType: quotation.discountType,
          vat: quotation.vat,
          vatRate: quotation.vatRate,
          installationCharges: quotation.installationCharges,
          total: quotation.total,
          amountPaid: 0,
          balance: quotation.total,
          paymentStatus: 'pending',
          payments: [],
          status: 'draft',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: get().currentUser,
        };

        // Auto-deduct inventory
        const updatedProducts = [...get().products];
        for (const item of quotation.items) {
          const productIdx = updatedProducts.findIndex(p => p.id === item.productId);
          if (productIdx >= 0) {
            updatedProducts[productIdx] = {
              ...updatedProducts[productIdx],
              reservedStock: updatedProducts[productIdx].reservedStock + item.quantity,
            };
          }
        }

        set((s) => ({
          invoices: [newInvoice, ...s.invoices],
          products: updatedProducts,
          quotations: s.quotations.map((q) => (q.id === quotationId ? { ...q, status: 'converted' as const, invoiceId: newInvoiceId } : q)),
        }));

        get().addActivityLog({
          id: generateId(), timestamp: new Date().toISOString(), user: get().currentUser,
          action: 'Quotation Converted', details: `${quotation.number} → ${newInvoice.number}`, status: 'success',
        });

        return newInvoiceId;
      },

      // Invoices
      invoices: sampleInvoices,
      addInvoice: (invoice) => {
        set((s) => ({ invoices: [invoice, ...s.invoices] }));
        get().addActivityLog({
          id: generateId(), timestamp: new Date().toISOString(), user: get().currentUser,
          action: 'Invoice Created', details: `${invoice.number} for ${invoice.customerName}`, status: 'success',
        });
      },
      updateInvoice: (id, data) => set((s) => ({
        invoices: s.invoices.map((inv) => (inv.id === id ? { ...inv, ...data, updatedAt: new Date().toISOString() } : inv)),
      })),
      addPayment: (invoiceId, payment) => {
        const state = get();
        const invoice = state.invoices.find((inv) => inv.id === invoiceId);
        if (!invoice) return;

        const newAmountPaid = invoice.amountPaid + payment.amount;
        const newBalance = invoice.total - newAmountPaid;
        const newPaymentStatus = newBalance <= 0 ? 'paid' : newAmountPaid > 0 ? 'partial' : 'pending';
        const isFullyPaid = newBalance <= 0;

        // Auto-deduct inventory when invoice is paid
        const updatedProducts = [...state.products];
        if (isFullyPaid) {
          for (const item of invoice.items) {
            const productIdx = updatedProducts.findIndex(p => p.id === item.productId);
            if (productIdx >= 0) {
              updatedProducts[productIdx] = {
                ...updatedProducts[productIdx],
                stock: updatedProducts[productIdx].stock - item.quantity,
                reservedStock: Math.max(0, updatedProducts[productIdx].reservedStock - item.quantity),
              };
            }
          }
        }

        // Auto-create finance record
        const newFinanceRecord: FinanceRecord = {
          id: generateId(), type: 'revenue', category: 'Invoice Payment',
          amount: payment.amount, description: `Payment from ${invoice.customerName} (${invoice.number})`,
          date: new Date().toISOString(), reference: invoice.number, paymentMethod: payment.method,
        };

        // Auto-create warranty when fully paid
        const newWarranties: Warranty[] = [];
        if (isFullyPaid) {
          for (const item of invoice.items) {
            const product = state.products.find(p => p.id === item.productId);
            if (product && product.warranty > 0) {
              const installDate = new Date().toISOString();
              const expiryDate = new Date();
              expiryDate.setMonth(expiryDate.getMonth() + product.warranty);
              newWarranties.push({
                id: generateId(),
                invoiceId: invoice.id,
                customerId: invoice.customerId,
                customerName: invoice.customerName,
                products: [{ productId: item.productId || '', productName: item.productName, serialNumber: `SN-${generateId()}` }],
                installationDate: installDate,
                expiryDate: expiryDate.toISOString(),
                technician: 'System',
                status: 'active',
                certificateGenerated: false,
              });
            }
          }
        }

        set((s) => ({
          invoices: s.invoices.map((inv) =>
            inv.id === invoiceId
              ? { ...inv, payments: [...inv.payments, payment], amountPaid: newAmountPaid, balance: newBalance, paymentStatus: newPaymentStatus as 'paid' | 'pending' | 'partial', status: isFullyPaid ? 'paid' as const : inv.status, updatedAt: new Date().toISOString() }
              : inv
          ),
          products: updatedProducts,
          financeRecords: [newFinanceRecord, ...s.financeRecords],
          warranties: newWarranties.length > 0 ? [...newWarranties, ...s.warranties] : s.warranties,
        }));

        state.addActivityLog({
          id: generateId(), timestamp: new Date().toISOString(), user: state.currentUser,
          action: 'Payment Received', details: `Rs. ${payment.amount.toLocaleString()} for ${invoice.number}`, status: 'success',
        });
      },

      // Finance
      financeRecords: sampleFinance,
      addFinanceRecord: (record) => {
        set((s) => ({ financeRecords: [record, ...s.financeRecords] }));
        get().addActivityLog({
          id: generateId(), timestamp: new Date().toISOString(), user: get().currentUser,
          action: record.type === 'revenue' ? 'Revenue Added' : 'Expense Recorded',
          details: `${record.description} - Rs. ${record.amount.toLocaleString()}`, status: 'info',
        });
      },

      // Warranties
      warranties: sampleWarranties,
      addWarranty: (warranty) => set((s) => ({ warranties: [warranty, ...s.warranties] })),
      updateWarranty: (id, data) => set((s) => ({
        warranties: s.warranties.map((w) => (w.id === id ? { ...w, ...data } : w)),
      })),

      // Activity Logs
      activityLogs: sampleActivityLogs,
      addActivityLog: (log) => set((s) => ({ activityLogs: [log, ...s.activityLogs].slice(0, 100) })),

      // Sidebar
      sidebarCollapsed: false,
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),

      // Theme
      theme: 'light',
      toggleTheme: () => set((s) => ({ theme: s.theme === 'light' ? 'dark' : 'light' })),

      // Reset to seed data (for dev/testing)
      resetToSeedData: () => set({
        customers: sampleCustomers,
        products: sampleProducts,
        suppliers: sampleSuppliers,
        staff: sampleStaff,
        projects: sampleProjects,
        quotations: sampleQuotations,
        invoices: sampleInvoices,
        financeRecords: sampleFinance,
        activityLogs: sampleActivityLogs,
        warranties: sampleWarranties,
      }),

      // Reset business data (keeps products/categories/brands)
      resetBusinessData: () => set({
        customers: [],
        projects: [],
        quotations: [],
        invoices: [],
        financeRecords: [],
        warranties: [],
        activityLogs: [],
      }),
    }),
    {
      name: 'ktec-admin-store',
      storage: createJSONStorage(() => localStorage),
      // Only persist data, not actions
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        currentUser: state.currentUser,
        sessionExpiry: state.sessionExpiry,
        customers: state.customers,
        products: state.products,
        suppliers: state.suppliers,
        staff: state.staff,
        projects: state.projects,
        quotations: state.quotations,
        invoices: state.invoices,
        financeRecords: state.financeRecords,
        warranties: state.warranties,
        activityLogs: state.activityLogs,
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
      }),
    }
  )
);
